import { CompactEncrypt, JWK, importJWK } from 'jose';

export const AUTH_DATA_KEY_ALG = 'RSA-OAEP-256';
export const AUTH_DATA_CONTENT_ENC = 'A256GCM';

export class SecureContextError extends Error {
  constructor() {
    super(
      'Web Crypto is unavailable: the checkout must be served from a secure (HTTPS) context to encrypt card data.',
    );
    this.name = 'SecureContextError';
  }
}

export interface EncryptionKey {
  key: CryptoKey;
  kid: string;
}

/**
 * Encrypts card data to the payment-engine's public key as a JWE compact string.
 * Nothing secret lives in the bundle: only the server holds the private key.
 */
export class CryptoHelper {
  static assertSecureContext(): void {
    if (!globalThis.crypto?.subtle) {
      throw new SecureContextError();
    }
  }

  static async importPublicKey(jwk: JWK): Promise<EncryptionKey> {
    CryptoHelper.assertSecureContext();

    if (jwk?.kty !== 'RSA' || !jwk.n || !jwk.e) {
      throw new Error('SDK public key is not a valid RSA JWK');
    }
    if (typeof jwk.kid !== 'string' || !jwk.kid) {
      throw new Error('SDK public key is missing its kid');
    }

    const key = await importJWK(jwk, AUTH_DATA_KEY_ALG);
    if (key instanceof Uint8Array) {
      throw new Error('SDK public key imported as a symmetric key');
    }
    return { key, kid: jwk.kid };
  }

  static async encryptAuthData(
    { key, kid }: EncryptionKey,
    payload: unknown,
  ): Promise<string> {
    CryptoHelper.assertSecureContext();

    // Copied into this realm's Uint8Array: jose checks `instanceof`, which
    // fails across realms (e.g. Node's TextEncoder under jsdom).
    const plaintext = new Uint8Array(
      new TextEncoder().encode(JSON.stringify(payload)),
    );
    return new CompactEncrypt(plaintext)
      .setProtectedHeader({
        alg: AUTH_DATA_KEY_ALG,
        enc: AUTH_DATA_CONTENT_ENC,
        kid,
      })
      .encrypt(key);
  }
}
